import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Html5Qrcode } from 'html5-qrcode';
import { motion, AnimatePresence } from 'framer-motion';
import { Camera, X, MagnifyingGlass, Package, Warning, Barcode } from '@phosphor-icons/react';
import { Loader2 } from 'lucide-react';
import axios from 'axios';
import { B2BonixLogo } from '@/components/B2BonixLogo';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function BarcodeSearchPage() {
  const navigate = useNavigate();
  const [barcode, setBarcode] = useState('');
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const scannerRef = useRef(null);
  const html5QrCodeRef = useRef(null);

  // Cleanup scanner on unmount
  useEffect(() => {
    return () => {
      if (html5QrCodeRef.current && html5QrCodeRef.current.isScanning) {
        html5QrCodeRef.current.stop().catch(console.error);
      }
    };
  }, []);

  const searchProduct = async (code) => {
    if (!code || code.trim() === '') return;
    
    setLoading(true);
    setError(null);
    setProduct(null);
    setHasSearched(true);

    try {
      const response = await axios.get(`${API_URL}/api/products/barcode/${code.trim()}`);
      setProduct(response.data);
    } catch (err) {
      if (err.response?.status === 404) {
        setError('Ürün bulunamadı');
      } else {
        setError('Bir hata oluştu. Lütfen tekrar deneyin.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    searchProduct(barcode);
  };

  const startScanner = async () => {
    setScanning(true);
    setError(null);
    
    // Wait for DOM element to render
    await new Promise(resolve => setTimeout(resolve, 300));
    
    try {
      // First check if getUserMedia is available
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        throw new Error('NOT_SUPPORTED');
      }

      // Request camera permission explicitly first
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "environment" } });
        // Stop the test stream immediately
        stream.getTracks().forEach(track => track.stop());
      } catch (permErr) {
        if (permErr.name === 'NotAllowedError') {
          throw new Error('PERMISSION_DENIED');
        }
        // If environment camera fails, try any camera
        try {
          const stream = await navigator.mediaDevices.getUserMedia({ video: true });
          stream.getTracks().forEach(track => track.stop());
        } catch {
          throw new Error('NO_CAMERA');
        }
      }

      html5QrCodeRef.current = new Html5Qrcode("barcode-scanner");
      
      // Try to get available cameras
      let cameraConfig = { facingMode: "environment" };
      try {
        const cameras = await Html5Qrcode.getCameras();
        if (cameras && cameras.length > 0) {
          // Prefer back camera
          const backCam = cameras.find(c => c.label?.toLowerCase().includes('back') || c.label?.toLowerCase().includes('arka'));
          if (backCam) {
            cameraConfig = backCam.id;
          }
        }
      } catch {
        // Fall back to facingMode
      }

      await html5QrCodeRef.current.start(
        cameraConfig,
        {
          fps: 10,
          qrbox: { width: 250, height: 100 },
          aspectRatio: 1.5,
        },
        (decodedText) => {
          setBarcode(decodedText);
          stopScanner();
          searchProduct(decodedText);
        },
        () => {}
      );
    } catch (err) {
      setScanning(false);
      if (err.message === 'PERMISSION_DENIED') {
        setError('Kamera izni reddedildi. Lütfen tarayıcı ayarlarından kamera iznini açın.');
      } else if (err.message === 'NOT_SUPPORTED') {
        setError('Tarayıcınız kamera erişimini desteklemiyor. Lütfen HTTPS üzerinden deneyin.');
      } else if (err.message === 'NO_CAMERA') {
        setError('Kamera bulunamadı. Lütfen cihazınızda kamera olduğundan emin olun.');
      } else {
        setError('Kamera başlatılamadı. Lütfen sayfayı yenileyip tekrar deneyin.');
      }
    }
  };

  const stopScanner = async () => {
    try {
      if (html5QrCodeRef.current) {
        if (html5QrCodeRef.current.isScanning) {
          await html5QrCodeRef.current.stop();
        }
        html5QrCodeRef.current.clear();
        html5QrCodeRef.current = null;
      }
    } catch (e) {
      console.warn('Scanner stop error:', e);
    }
    setScanning(false);
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency: 'TRY',
      minimumFractionDigits: 2,
    }).format(price);
  };

  return (
    <div 
      className="min-h-screen flex flex-col"
      style={{
        background: 'linear-gradient(180deg, #283086 0%, #1a224f 55%, #12173a 100%)',
      }}
      data-testid="barcode-search-page"
    >
      {/* Header */}
      <header 
        className="py-4 px-6 md:px-12 flex justify-between items-center border-b border-white/10"
        data-testid="header"
      >
        <a href="/" className="flex items-center gap-3" data-testid="logo-link">
          <B2BonixLogo variant="light" iconSize={40} asLink={false} />
        </a>
        <button
          onClick={() => navigate('/login')}
          className="bg-white text-[#1a224f] px-6 py-2.5 font-semibold text-sm rounded-lg hover:bg-white/90 transition-colors"
          data-testid="dealer-login-btn"
        >
          Bayi Girişi
        </button>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex flex-col items-center justify-center px-4 py-12">
        {/* Title */}
        <div className="text-center mb-8">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-3">
            Barkod ile Ürün Sorgula
          </h1>
          <p className="text-white/80 text-lg">
            Ürün barkodunu girerek detaylı bilgilere ulaşın
          </p>
        </div>

        {/* Search Card */}
        <div 
          className="w-full max-w-lg bg-white rounded-2xl shadow-2xl p-8"
          data-testid="search-card"
        >
          <form onSubmit={handleSubmit}>
            {/* Input Label */}
            <label className="block text-center text-gray-800 font-semibold text-lg mb-4">
              Barkod Numarası
            </label>
            
            {/* Input Field */}
            <div className="relative mb-4">
              <input
                type="text"
                value={barcode}
                onChange={(e) => setBarcode(e.target.value)}
                placeholder="Örnek: 9786051234567"
                className="w-full px-4 py-4 text-lg border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1a224f]/20 focus:border-[#1a224f] transition-all pr-12"
                data-testid="barcode-input"
              />
              <MagnifyingGlass 
                weight="regular" 
                className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400"
              />
            </div>

            {/* Search Button */}
            <button
              type="submit"
              disabled={loading || !barcode.trim()}
              className="w-full bg-[#1a224f] text-white py-4 rounded-lg font-semibold text-lg hover:bg-[#12173a] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              data-testid="search-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Aranıyor...
                </>
              ) : (
                'Sorgula'
              )}
            </button>

            {/* Divider */}
            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-200"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="bg-white px-4 text-gray-500">VEYA</span>
              </div>
            </div>

            {/* Camera Button */}
            <button
              type="button"
              onClick={scanning ? stopScanner : startScanner}
              className={`w-full py-4 rounded-lg font-semibold text-base flex items-center justify-center gap-3 transition-all ${
                scanning 
                  ? 'bg-gray-800 text-white' 
                  : 'bg-white border-2 border-gray-200 text-gray-700 hover:border-[#1a224f] hover:text-[#1a224f]'
              }`}
              data-testid="camera-scan-btn"
            >
              {scanning ? (
                <>
                  <X weight="bold" className="w-5 h-5" />
                  Taramayı Durdur
                </>
              ) : (
                <>
                  <Camera weight="regular" className="w-5 h-5" />
                  Barkodu Kamera ile Okut
                </>
              )}
            </button>
          </form>

          {/* Camera Scanner - always in DOM so Html5Qrcode can find it */}
          <div 
            className={`mt-6 rounded-lg overflow-hidden border border-gray-200 transition-all duration-300 ${scanning ? 'opacity-100' : 'hidden'}`}
          >
            <div 
              id="barcode-scanner" 
              ref={scannerRef}
              className="w-full"
              style={{ minHeight: scanning ? '250px' : '0' }}
            />
            <p className="text-center py-2 bg-gray-100 text-gray-600 text-sm">
              Barkodu kameraya gösterin
            </p>
          </div>
        </div>

        {/* Error State */}
        <AnimatePresence>
          {error && !loading && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="mt-8 bg-white rounded-2xl shadow-xl p-8 max-w-lg w-full text-center"
              data-testid="error-state"
            >
              <Warning weight="regular" className="w-16 h-16 mx-auto text-orange-500 mb-4" />
              <p className="text-xl font-semibold text-gray-800">{error}</p>
              <p className="mt-2 text-gray-500">
                Lütfen barkod numarasını kontrol edip tekrar deneyin.
              </p>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Product Result */}
        <AnimatePresence>
          {product && !loading && (
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -30 }}
              transition={{ duration: 0.4, ease: [0.23, 1, 0.32, 1] }}
              className="mt-8 bg-white rounded-2xl shadow-xl overflow-hidden max-w-2xl w-full"
              data-testid="product-result"
            >
              <div className="flex flex-col md:flex-row">
                {/* Image */}
                {product.image_url && (
                  <div className="md:w-1/3 bg-gray-50 p-6 flex items-center justify-center">
                    <img
                      src={product.image_url}
                      alt={product.name}
                      className="max-h-48 object-contain rounded shadow-lg"
                      data-testid="product-image"
                    />
                  </div>
                )}
                
                {/* Info */}
                <div className={`flex-1 p-6 ${product.image_url ? '' : 'w-full'}`}>
                  {/* Barcode */}
                  <div className="flex items-center gap-2 text-sm text-gray-500 mb-2">
                    <Barcode weight="regular" className="w-4 h-4" />
                    <span className="font-mono">{product.barcode}</span>
                  </div>
                  
                  {/* Name */}
                  <h2 
                    className="text-2xl font-bold text-gray-900 mb-4"
                    data-testid="product-name"
                  >
                    {product.name}
                  </h2>
                  
                  {/* Price & Stock */}
                  <div className="flex flex-wrap gap-6 mb-4">
                    <div>
                      <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Fiyat</p>
                      <p 
                        className="text-2xl font-bold text-[#1a224f]"
                        data-testid="product-price"
                      >
                        {formatPrice(product.price)}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Stok</p>
                      <div 
                        className={`flex items-center gap-2 text-lg font-semibold ${
                          product.stock > 0 ? 'text-green-600' : 'text-red-600'
                        }`}
                        data-testid="product-stock"
                      >
                        <span className={`w-2.5 h-2.5 rounded-full ${
                          product.stock > 0 ? 'bg-green-500' : 'bg-red-500'
                        }`} />
                        {product.stock > 0 ? `${product.stock} Adet` : 'Stokta Yok'}
                      </div>
                    </div>
                  </div>
                  
                  {/* Description */}
                  {product.description && (
                    <div className="pt-4 border-t border-gray-100">
                      <p className="text-xs text-gray-500 uppercase tracking-wider mb-2">Açıklama</p>
                      <div 
                        className="text-gray-600 text-sm prose prose-sm max-w-none"
                        data-testid="product-description"
                        dangerouslySetInnerHTML={{ __html: product.description }}
                      />
                    </div>
                  )}
                  
                  {/* Additional Info */}
                  {(product.author || product.publisher || product.isbn) && (
                    <div className="mt-4 pt-4 border-t border-gray-100 grid grid-cols-2 gap-4">
                      {product.author && (
                        <div>
                          <p className="text-xs text-gray-500 uppercase tracking-wider">Yazar</p>
                          <p className="text-sm text-gray-800" data-testid="product-author">{product.author}</p>
                        </div>
                      )}
                      {product.publisher && (
                        <div>
                          <p className="text-xs text-gray-500 uppercase tracking-wider">Yayınevi</p>
                          <p className="text-sm text-gray-800" data-testid="product-publisher">{product.publisher}</p>
                        </div>
                      )}
                      {product.isbn && (
                        <div className="col-span-2">
                          <p className="text-xs text-gray-500 uppercase tracking-wider">ISBN</p>
                          <p className="text-sm font-mono text-gray-800" data-testid="product-isbn">{product.isbn}</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </main>

      {/* Footer */}
      <footer className="py-6 text-center border-t border-white/10">
        <p className="text-white/70 text-sm">
          &copy; 2026 B2Bonix by WebPlace. Tüm hakları saklıdır.
        </p>
      </footer>
    </div>
  );
}
